import toast from "react-hot-toast";

import { useAuthStore } from "@/stores/AuthStore";
import { useOrganisationStore } from "@/stores/OrganisationStore";
import { refreshSession } from "@/Utils/Auth/authSession";
import { createTokenRefresher } from "@/Utils/Auth/createTokenRefresher";
import { loggerService, LogTag } from "@/Utils/LoggerService";

import { namedBlobFromResponse } from "./contentDisposition";

// Singleton refresh guard: concurrent 401s trigger a single cookie refresh.
// The browser carries the auth tokens in httpOnly cookies, so the refresh
// endpoint rotates and re-sets them server-side — nothing is read in JS.
const tryRefreshTokens = createTokenRefresher(() => refreshSession());

// Only the organisation context header is attached client-side; the auth tokens
// ride in the httpOnly cookies sent via credentials: "include".
function buildHeaders(orgId: number | undefined, base?: HeadersInit): Headers {
  const headers = new Headers(base ?? undefined);

  if (orgId && !headers.has("x-organisation-id")) {
    headers.set("x-organisation-id", String(orgId));
  }

  return headers;
}

async function appFetch(route: string, options?: RequestInit) {
  try {
    const { clearUser } = useAuthStore.getState();
    const { clearOrganisation } = useOrganisationStore.getState();

    const handleSessionExpired = () => {
      clearUser();
      clearOrganisation();
      return { unauthorized: true };
    };

    const { organisation } = useOrganisationStore.getState();
    const response = await fetch(route, {
      ...options,
      headers: buildHeaders(organisation?.org_id, options?.headers),
      credentials: "include",
    });

    if (response.status === 429) {
      toast.error("Trop de requêtes, veuillez réessayer plus tard");
      return null;
    }

    // On 401, attempt one refresh (via the refresh cookie) + retry.
    if (response.status === 401) {
      const refreshed = await tryRefreshTokens();
      if (!refreshed) {
        return handleSessionExpired();
      }

      const { organisation: newOrg } = useOrganisationStore.getState();
      const retryResponse = await fetch(route, {
        ...options,
        headers: buildHeaders(newOrg?.org_id, options?.headers),
        credentials: "include",
      });

      if (retryResponse.status === 401) {
        return handleSessionExpired();
      }

      if (retryResponse.ok) {
        const retryContentType = retryResponse.headers.get("content-type");
        if (
          retryContentType?.includes("application/pdf") ||
          retryContentType?.includes("text/csv")
        ) {
          return await namedBlobFromResponse(retryResponse);
        }
        return await retryResponse.json();
      }

      return null;
    }

    const contentType = response.headers.get("content-type");

    if (response.ok) {
      if (
        contentType?.includes("application/pdf") ||
        contentType?.includes("text/csv")
      ) {
        return await namedBlobFromResponse(response);
      }
      return await response.json();
    }

    const parseResponseData = async () => {
      if (!contentType?.includes("application/json")) return null;
      return await response.json();
    };

    const responseData = await parseResponseData();
    loggerService.error(LogTag.API, "API request failed", {
      route,
      responseData,
      status: response.status,
    });

    const firstDetail =
      Array.isArray(responseData?.details) && responseData.details.length > 0
        ? (responseData.details[0] as { message?: string }).message
        : null;

    if (response.status === 403) {
      toast.error(firstDetail ?? responseData?.message ?? "Accès refusé");
      return { forbidden: true };
    }

    toast.error(
      firstDetail ?? responseData?.message ?? "Une erreur est survenue",
    );
    return null;
  } catch (error) {
    loggerService.error(LogTag.API, "Unexpected fetch error", error);
    toast.error("Une erreur est survenue");
    return null;
  }
}

export { appFetch };
export default appFetch;
